import { useCallback } from 'react';
import { useEventListener } from './use-event-listener';

/**
 * Creates a custom event system with typed events.
 *
 * @template EventInterface - An interface that maps event names to their corresponding data types.
 *
 * @returns An object containing:
 * - `triggerCustomEvent`: A function to trigger a custom event.
 * - `addCustomEventListener`: A function to add a global event listener (can be called from anywhere).
 * - `useCustomEventListener`: A hook to listen for custom events (React components only).
 *
 * @example
 * interface MyEvents {
 *   eventName: { someData: string };
 *   anotherEvent: null; // Event without details
 * }
 *
 * const { triggerCustomEvent, addCustomEventListener, useCustomEventListener } = createCustomEventSystem<MyEvents>();
 *
 * // Triggering an event with details
 * triggerCustomEvent('eventName', { someData: 'example' });
 *
 * // Triggering an event without details
 * triggerCustomEvent('anotherEvent');
 *
 * // Using the global event listener (can be called from anywhere)
 * const removeListener = addCustomEventListener('eventName', (data) => {
 *   console.log(data.someData);
 * });
 *
 * // Remove the listener when needed
 * removeListener();
 *
 * // Using the event listener hook (React components only)
 * useCustomEventListener('eventName', (data) => {
 *   console.log(data.someData);
 * });
 *
 * useCustomEventListener('anotherEvent', () => {
 *   console.log('anotherEvent triggered');
 * });
 */
export function createCustomEventSystem<EventInterface extends Record<string, any>>() {
  // Trigger custom event function
  const triggerCustomEvent = <EventName extends keyof EventInterface>(
    eventName: EventName,
    data?: EventInterface[EventName]
  ) => {
    const event = new CustomEvent(eventName as string, { detail: data });
    window.dispatchEvent(event);
  };

  // Global event listener function (can be called from anywhere)
  const addCustomEventListener = <EventName extends keyof EventInterface>(
    eventName: EventName,
    handler: (data: EventInterface[EventName]) => void | Promise<void>,
    options?: boolean | AddEventListenerOptions
  ) => {
    const eventHandler = (e: CustomEvent<EventInterface[EventName]>) => handler(e.detail);

    window.addEventListener(eventName as string, eventHandler as EventListener, options);

    // Return a function to remove the listener
    return () => {
      window.removeEventListener(eventName as string, eventHandler as EventListener, options);
    };
  };

  // Custom event listener hook
  const useCustomEventListener = <EventName extends keyof EventInterface>(
    eventName: EventName,
    handler: (data: EventInterface[EventName]) => void | Promise<void>,
    isListenerActive = true
  ) => {
    const eventHandler = useCallback((e: CustomEvent<EventInterface[EventName]>) => handler(e.detail), [handler]);

    useEventListener(eventName as any, eventHandler, isListenerActive);
  };

  return { triggerCustomEvent, addCustomEventListener, useCustomEventListener };
}
